import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";

// Custom olive pin to match the brand
const olivePin = L.divIcon({
  className: "twtg-pin",
  html: `<span class="twtg-pin-dot"></span>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

// Olive cluster bubble
const createClusterIcon = (cluster) =>
  L.divIcon({
    className: "twtg-cluster",
    html: `<div class="twtg-cluster-inner">${cluster.getChildCount()}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });

export default function MapPage() {
  const [pins, setPins] = useState([]);

  useEffect(() => {
    api.get("/map").then(r => setPins(r.data)).catch(() => setPins([]));
  }, []);

  return (
    <div data-testid="map-page">
      <Helmet>
        <title>Destinations Map · The Where To Go</title>
        <meta name="description" content="Explore every destination featured on The Where To Go on an interactive world map. Click any pin to read the article." />
      </Helmet>

      <section className="max-w-8xl mx-auto px-6 md:px-12 pt-10 pb-6">
        <div className="overline text-muted">The Where To Go</div>
        <h1 className="font-serif text-4xl md:text-6xl mt-2">Destinations Map</h1>
        <p className="mt-3 text-muted max-w-2xl">Every city, country and corner we've covered, plotted across the globe. Click a pin to open the article.</p>
      </section>

      <section className="max-w-8xl mx-auto px-6 md:px-12 pb-16">
        <div className="w-full h-[70vh] min-h-[480px] border border-line overflow-hidden" data-testid="map-container">
          <MapContainer center={[30, 20]} zoom={2} minZoom={2} maxZoom={16} scrollWheelZoom={true} style={{ height: "100%", width: "100%", background: "#f4f1ea" }}>
            <TileLayer
              attribution='Tiles &copy; <a href="https://www.esri.com" target="_blank" rel="noreferrer">Esri</a> &mdash; National Geographic, DeLorme, NAVTEQ'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/NatGeo_World_Map/MapServer/tile/{z}/{y}/{x}"
              maxZoom={16}
            />
            {pins.length > 0 && (
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterIcon}
                showCoverageOnHover={false}
                spiderfyOnMaxZoom={true}
                zoomToBoundsOnClick={true}
                maxClusterRadius={25}
                disableClusteringAtZoom={8}
              >
                {pins.map((p, idx) => (
                  <Marker key={`${p.slug}-${idx}`} position={[p.lat, p.lng]} icon={olivePin}>
                    <Popup minWidth={220} maxWidth={260}>
                      <Link to={`/articles/${p.slug}`} className="block group" data-testid={`map-popup-${p.slug}-${idx}`}>
                        <img src={p.card_cover} alt={p.title} className="w-full h-32 object-cover" />
                        <div className="pt-2">
                          <div className="overline text-muted">{p.location_name}</div>
                          <div className="font-serif text-lg leading-tight mt-1 group-hover:text-olive">{p.title}</div>
                          <div className="text-xs text-muted italic">{p.subtitle}</div>
                          <div className="text-[0.65rem] uppercase tracking-[0.18em] text-olive font-semibold mt-2">Read article →</div>
                        </div>
                      </Link>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        </div>
        <p className="mt-3 text-xs text-muted">{pins.length} pins across {new Set(pins.map(p => p.slug)).size} articles. Drag to pan, scroll to zoom.</p>
      </section>
    </div>
  );
}
